import { LitElement, html, css } from "lit";
import { customElement, state } from "lit/decorators.js";
import { shared } from "../styles.js";
import { createPat, listPats, revokePat, type PatInfo } from "../api/account.js";
import { humanError } from "../api/errors.js";

/**
 * settings-tokens manages personal access tokens (PATs): the credentials the
 * `bino registry login` CLI stores. A token's plaintext is shown exactly once at
 * creation (create → copy → it is never retrievable again). Expiry is chosen from
 * a small preset; the server accepts an expiresIn in seconds (0 = never).
 */
@customElement("settings-tokens")
export class SettingsTokens extends LitElement {
  static override styles = [
    shared,
    css`
      .tok-list {
        list-style: none;
        margin: var(--sp-2) 0;
        padding: 0;
      }
      .tok-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        padding: 8px 0;
        border-bottom: 1px solid var(--border);
      }
      .tok-row:last-child {
        border-bottom: none;
      }
      .tok-meta {
        font-size: var(--fs-1);
      }
      .create {
        display: flex;
        gap: 8px;
        align-items: flex-end;
        margin-top: var(--sp-3);
        flex-wrap: wrap;
      }
      .create .field {
        margin: 0;
        flex: 1 1 160px;
      }
      .new-token {
        margin: var(--sp-3) 0;
        padding: var(--sp-3);
        border: 1px solid var(--pos);
        border-radius: var(--radius, 8px);
      }
      .new-token code {
        display: block;
        word-break: break-all;
        font-family: var(--mono);
        margin: var(--sp-2) 0;
      }
    `,
  ];

  @state() private tokens: PatInfo[] = [];
  @state() private name = "";
  @state() private expiry = "0";
  @state() private busy = false;
  @state() private errorMsg = "";
  @state() private plaintext = "";

  override connectedCallback() {
    super.connectedCallback();
    void this.load();
  }

  private async load() {
    try {
      this.tokens = await listPats();
    } catch (err) {
      this.errorMsg = humanError(err);
    }
  }

  private async create() {
    const name = this.name.trim();
    if (!name) {
      this.errorMsg = "Give the token a name.";
      return;
    }
    this.errorMsg = "";
    this.busy = true;
    try {
      const created = await createPat(name, Number(this.expiry));
      this.plaintext = created.token;
      this.name = "";
      await this.load();
    } catch (err) {
      this.errorMsg = humanError(err);
    } finally {
      this.busy = false;
    }
  }

  private async revoke(id: string) {
    this.errorMsg = "";
    this.busy = true;
    try {
      await revokePat(id);
      await this.load();
    } catch (err) {
      this.errorMsg = humanError(err);
    } finally {
      this.busy = false;
    }
  }

  private async copy() {
    try {
      await navigator.clipboard.writeText(this.plaintext);
    } catch {
      /* clipboard blocked — the token is visible to select manually */
    }
  }

  override render() {
    return html`
      <div class="card">
        <h2>Access tokens</h2>
        <p class="muted">
          Personal access tokens authenticate the <span class="mono">bino</span> CLI. Create one
          here, then run <span class="mono">bino registry login</span> and paste it.
        </p>
        ${this.errorMsg ? html`<div class="error" role="alert">${this.errorMsg}</div>` : ""}
        ${this.plaintext ? this.renderNewToken() : ""}
        ${this.tokens.length
          ? html`<ul class="tok-list">
              ${this.tokens.map(
                (t) => html`<li class="tok-row">
                  <span
                    >${t.name}
                    <span class="muted tok-meta"
                      >· <span class="mono">${t.prefix}…</span>
                      ${t.expires ? html`· expires ${t.expires.slice(0, 10)}` : "· never expires"}</span
                    ></span
                  >
                  <button
                    class="btn btn-danger"
                    ?disabled=${this.busy}
                    @click=${() => this.revoke(t.id)}
                  >
                    Revoke
                  </button>
                </li>`,
              )}
            </ul>`
          : html`<p class="muted">No tokens yet.</p>`}
        <div class="create">
          <div class="field">
            <label for="tok-name">Name</label>
            <input
              id="tok-name"
              placeholder="e.g. laptop CLI"
              .value=${this.name}
              @input=${(e: Event) => (this.name = (e.target as HTMLInputElement).value)}
            />
          </div>
          <div class="field">
            <label for="tok-exp">Expires</label>
            <select
              id="tok-exp"
              .value=${this.expiry}
              @change=${(e: Event) => (this.expiry = (e.target as HTMLSelectElement).value)}
            >
              <option value="0">Never</option>
              <option value="2592000">30 days</option>
              <option value="7776000">90 days</option>
              <option value="31536000">1 year</option>
            </select>
          </div>
          <button class="btn btn-primary" ?disabled=${this.busy} @click=${this.create}>
            ${this.busy ? "Creating…" : "Create token"}
          </button>
        </div>
      </div>
    `;
  }

  private renderNewToken() {
    return html`
      <div class="new-token">
        <strong>Copy your token now — it won't be shown again.</strong>
        <code>${this.plaintext}</code>
        <button class="btn" @click=${this.copy}>Copy</button>
        <button class="btn" @click=${() => (this.plaintext = "")}>Done</button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "settings-tokens": SettingsTokens;
  }
}
